import Link from "next/link";
import { CalendarDays, Clock3, MapPin } from "lucide-react";
import { fetchEvents } from "@/lib/api";
import type { SiteTemplateKey } from "@/lib/siteTemplates";

function dateLabel(value: string, allDay: boolean) {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(allDay ? {} : { hour: "numeric", minute: "2-digit" }),
  }).format(date);
}

export async function EventsPreviewBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "Upcoming Events");
  const subtitle = String(content.subtitle ?? "");
  const limit = typeof content.limit === "number" ? content.limit : 6;

  const events = await fetchEvents().catch(() => []);
  const preview = events.slice(0, limit);

  return (
    <section data-template={template} className="mx-auto max-w-7xl px-4 py-[calc(var(--space-unit)*4)]">
      <h2
        className="font-bold text-[var(--color-text)]"
        style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
      >
        {heading}
      </h2>
      {subtitle && <p className="mt-2 text-[var(--color-text)]/70">{subtitle}</p>}

      {preview.length === 0 ? (
        <p className="mt-6 text-[var(--color-text)]/60">No upcoming events are published yet.</p>
      ) : (
        <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {preview.map((event) => {
            const occurrence = event.occurrences[0];
            return (
              <li key={event.id}>
                <Link
                  href={`/events/${event.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-[rgb(var(--color-primary))]">
                    <CalendarDays className="h-4 w-4" aria-hidden />
                    {occurrence
                      ? dateLabel(occurrence.startAt, event.allDay)
                      : dateLabel(event.startAt, event.allDay)}
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-[var(--color-text)] group-hover:text-[rgb(var(--color-primary))]">
                    {event.title}
                  </h3>
                  {event.summary && <p className="mt-2 line-clamp-3 text-sm text-[var(--color-text)]/70">{event.summary}</p>}
                  <div className="mt-auto space-y-2 pt-5 text-sm text-[var(--color-text)]/70">
                    {!event.allDay && occurrence && (
                      <span className="flex items-center gap-2">
                        <Clock3 className="h-4 w-4" aria-hidden />
                        {dateLabel(occurrence.startAt, false)}
                      </span>
                    )}
                    {event.venueName && (
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" aria-hidden />
                        {event.venueName}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-6">
        <Link
          href="/events"
          className="inline-flex min-h-[44px] items-center justify-center rounded-[var(--border-radius)] bg-[rgb(var(--color-primary))] px-5 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          View all events
        </Link>
      </div>
    </section>
  );
}
